import React, { useState, useEffect } from 'react';
import {
  User,
  FileText,
  MessageSquare,
  Download,
  X,
  Calendar,
  Mail,
  Hash,
  Euro,
  FileCheck,
  Send,
  Bug,
  Lightbulb,
  Check,
  ChevronDown,
  Upload,
  Settings,
  Clock,
  FileSignature,
  ChevronRight,
  BarChart2,
  AlertTriangle
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr, nl, enUS } from 'date-fns/locale';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { InvoiceTemplate, getGroupCompanies, GroupCompany } from './InvoiceTemplate';

interface MemberSidebarProps {
  participant: any;
  user: any;
  onRefresh?: () => void;
}

type SidebarSection = 'profile' | 'invoices' | 'contact' | 'settings' | null;

interface UploadRecord {
  id: string;
  participant_id: string;
  participant_name: string;
  ean_code: string;
  file_name: string;
  upload_date: string;
  created_at: string;
}

export function MemberSidebar({ participant, user, onRefresh }: MemberSidebarProps) {
  const { t, i18n } = useTranslation();
  const [activeSection, setActiveSection] = useState<SidebarSection>(null);
  const [availableMonths, setAvailableMonths] = useState<{ month: string; year: number; monthNumber: number }[]>([]);
  const [loadingMonths, setLoadingMonths] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState<string | null>(null);
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [selectedInvoiceMonth, setSelectedInvoiceMonth] = useState<string | null>(null);
  const [selectedInvoiceCompany, setSelectedInvoiceCompany] = useState<GroupCompany | null>(null);
  const [selectedInvoicePeriod, setSelectedInvoicePeriod] = useState<{ startMonth: string; endMonth: string } | null>(null);
  const [selectedInvoiceIssueDate, setSelectedInvoiceIssueDate] = useState<string | null>(null);
  const [expandedQuarters, setExpandedQuarters] = useState<Set<string>>(new Set());
  const [groupCompanies, setGroupCompanies] = useState<GroupCompany[]>([]);
  const [expandedYears, setExpandedYears] = useState<Set<number>>(new Set());

  const [contactForm, setContactForm] = useState({
    type: 'support' as 'support' | 'bug' | 'suggestion',
    message: ''
  });
  const [sendingContact, setSendingContact] = useState(false);

  const [groupEans, setGroupEans] = useState<{ name: string; ean: string }[]>([]);
  const [showEanDropdown, setShowEanDropdown] = useState(false);
  const [groupPriceInfo, setGroupPriceInfo] = useState<{ hasConsumers: boolean; hasProducers: boolean; hasProsumers: boolean; sharedEnergyPrice: string | null; prosumerBuyPrice: string | null; prosumerSellPrice: string | null } | null>(null);

  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [uploadHistory, setUploadHistory] = useState<UploadRecord[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const [groupParticipants, setGroupParticipants] = useState<{ id: string; name: string; email: string; ean_code: string }[]>([]);
  const [selectedUploadParticipants, setSelectedUploadParticipants] = useState<string[]>([]);

  const [showContractUpload, setShowContractUpload] = useState(false);
  const [contractSelectedParticipants, setContractSelectedParticipants] = useState<string[]>([]);
  const [uploadingContract, setUploadingContract] = useState(false);
  const contractFileInputRef = React.useRef<HTMLInputElement>(null);

  const getDateLocale = () => {
    switch (i18n.language) {
      case 'nl': return nl;
      case 'en': return enUS;
      default: return fr;
    }
  };

  useEffect(() => {
    if (activeSection === 'invoices') {
      loadAvailableMonths();
    }
    if (activeSection === 'profile' && participant?.groupe) {
      loadGroupEans();
      loadGroupPriceInfo();
    }
    if (activeSection === 'settings') {
      loadUploadHistory();
      setShowContractUpload(false);
      setContractSelectedParticipants([]);
      if (participant?.groupe) {
        loadGroupParticipants();
      } else {
        setSelectedUploadParticipants([participant.id]);
        setContractSelectedParticipants([participant.id]);
      }
    }
  }, [activeSection, participant]);

  const loadAvailableMonths = async () => {
    setLoadingMonths(true);
    try {
      let monthlyDataKeys: string[] = [];

      if (participant?.groupe) {
        const { data: groupMembers } = await supabase
          .from('participants')
          .select('monthly_data, entry_date')
          .eq('groupe', participant.groupe);

        for (const member of groupMembers || []) {
          if (member.monthly_data) {
            monthlyDataKeys.push(...Object.keys(member.monthly_data));
          }
        }

        let earliestEntryDate: Date | null = null;
        if (groupMembers) {
          for (const member of groupMembers) {
            if (member.entry_date) {
              const d = parseISO(member.entry_date);
              if (!earliestEntryDate || d < earliestEntryDate) earliestEntryDate = d;
            }
          }
        }

        const companies = await getGroupCompanies(participant.groupe);
        setGroupCompanies(companies);

        const months = buildMonthsList(monthlyDataKeys, earliestEntryDate);
        setAvailableMonths(months);
      } else {
        if (participant?.monthly_data) {
          monthlyDataKeys = Object.keys(participant.monthly_data);
        }
        const entryDate = participant?.entry_date ? parseISO(participant.entry_date) : null;
        setAvailableMonths(buildMonthsList(monthlyDataKeys, entryDate));
      }

      setExpandedYears(new Set());
    } catch (error) {
      console.error('Error loading available months:', error);
      toast.error(t('memberUi.errLoadMonths'));
    } finally {
      setLoadingMonths(false);
    }
  };

  const buildMonthsList = (keys: string[], earliestEntryDate: Date | null) =>
    Array.from(new Set(keys))
      .filter(key => /^\d{4}-\d{2}$/.test(key))
      .map(key => {
        const [y, m] = key.split('-');
        return { month: key, year: parseInt(y), monthNumber: parseInt(m) };
      })
      .filter(m => {
        if (!earliestEntryDate) return true;
        const entryYear = earliestEntryDate.getFullYear();
        const entryMonth = earliestEntryDate.getMonth();
        if (m.year < entryYear) return false;
        if (m.year === entryYear && m.monthNumber - 1 < entryMonth) return false;
        return true;
      })
      .sort((a, b) => a.year !== b.year ? b.year - a.year : b.monthNumber - a.monthNumber);

  const handleGenerateInvoice = async (monthKey: string, company?: GroupCompany) => {
    setSelectedInvoiceMonth(monthKey);
    setSelectedInvoiceCompany(company || null);
    setSelectedInvoicePeriod({ startMonth: monthKey, endMonth: monthKey });
    const issueDate = await loadIssueDate(monthKey, monthKey, company);
    setSelectedInvoiceIssueDate(issueDate);
    setShowInvoicePreview(true);
  };

  const handleGenerateQuarterInvoice = async (startMonth: string, endMonth: string, company?: GroupCompany) => {
    setSelectedInvoiceMonth(null);
    setSelectedInvoiceCompany(company || null);
    setSelectedInvoicePeriod({ startMonth, endMonth });
    const issueDate = await loadIssueDate(startMonth, endMonth, company);
    setSelectedInvoiceIssueDate(issueDate);
    setShowInvoicePreview(true);
  };

  const loadIssueDate = async (startMonth: string, endMonth: string, company?: GroupCompany): Promise<string | null> => {
    try {
      const groupKey = participant?.groupe || null;
      const companyNumber = company?.company_number ?? null;
      let query = supabase
        .from('invoice_issue_dates')
        .select('issue_date')
        .eq('period_start', startMonth)
        .eq('period_end', endMonth);
      if (groupKey) {
        query = query.eq('group_key', groupKey);
      } else {
        query = query.is('group_key', null);
      }
      if (companyNumber) {
        query = query.eq('company_number', companyNumber);
      } else {
        query = query.is('company_number', null);
      }
      const { data, error } = await query.maybeSingle();
      if (error) {
        console.error('loadIssueDate query error:', error);
        return null;
      }
      console.log('loadIssueDate result:', { startMonth, endMonth, groupKey, companyNumber, data });
      return data?.issue_date || null;
    } catch (err) {
      console.error('loadIssueDate exception:', err);
      return null;
    }
  };

  const QUARTER_LABELS = ['1er trimestre', '2e trimestre', '3e trimestre', '4e trimestre'];

  const getQuarterMonths = (year: number, quarter: number) => {
    const startMonthNum = (quarter - 1) * 3 + 1;
    const months = [];
    for (let i = 0; i < 3; i++) {
      const m = startMonthNum + i;
      months.push({
        monthKey: `${year}-${String(m).padStart(2, '0')}`,
        monthNumber: m,
        year,
      });
    }
    return months;
  };

  const isQuarterComplete = (year: number, quarter: number, availableMonthKeys: Set<string>) => {
    const months = getQuarterMonths(year, quarter);
    return months.every(m => availableMonthKeys.has(m.monthKey));
  };

  const loadGroupEans = async () => {
    if (!participant?.groupe) return;

    try {
      const { data, error } = await supabase
        .from('participants')
        .select('name, ean_code')
        .eq('groupe', participant.groupe)
        .order('name');

      if (error) throw error;

      setGroupEans((data || []).filter(p => p.ean_code).map(p => ({
        name: p.name,
        ean: p.ean_code
      })));
    } catch (error) {
      console.error('Error loading group EANs:', error);
    }
  };

  const loadGroupPriceInfo = async () => {
    if (!participant?.groupe) {
      setGroupPriceInfo(null);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('participants')
        .select('type, shared_energy_price, prosumer_buy_price, prosumer_sell_price')
        .eq('groupe', participant.groupe);

      if (error) throw error;

      const members = data || [];
      const hasConsumers = members.some(p => p.type === 'consumer');
      const hasProducers = members.some(p => p.type === 'producer');
      const hasProsumers = members.some(p => p.type === 'prosumer');

      const isMixedGroup = (hasConsumers && hasProducers) || (hasConsumers && hasProsumers) || (hasProducers && hasProsumers);

      const prosumer = members.find(p => p.type === 'prosumer');

      setGroupPriceInfo({
        hasConsumers,
        hasProducers,
        hasProsumers,
        sharedEnergyPrice: isMixedGroup ? (prosumer?.prosumer_buy_price ?? prosumer?.shared_energy_price ?? members[0]?.shared_energy_price ?? null) : (members[0]?.shared_energy_price ?? null),
        prosumerBuyPrice: prosumer?.prosumer_buy_price ?? null,
        prosumerSellPrice: prosumer?.prosumer_sell_price ?? null,
      });
    } catch (error) {
      console.error('Error loading group price info:', error);
      setGroupPriceInfo(null);
    }
  };

  const loadGroupParticipants = async () => {
    if (!participant?.groupe) return;
    try {
      const { data, error } = await supabase
        .from('participants')
        .select('id, name, email, ean_code')
        .eq('groupe', participant.groupe)
        .order('name');

      if (error) throw error;
      setGroupParticipants(data || []);
      setSelectedUploadParticipants((data || []).map(p => p.id));
    } catch (error) {
      console.error('Error loading group participants:', error);
    }
  };

  const loadUploadHistory = async () => {
    setLoadingHistory(true);
    try {
      if (participant?.groupe) {
        const { data: groupMembers } = await supabase
          .from('participants')
          .select('id')
          .eq('groupe', participant.groupe);

        const ids = groupMembers && groupMembers.length > 0
          ? groupMembers.map(p => p.id)
          : [participant.id];

        const { data, error } = await supabase
          .from('supplier_invoice_uploads')
          .select('id, participant_id, participant_name, ean_code, file_name, upload_date, created_at')
          .in('participant_id', ids)
          .like('file_name', 'CONTRAT_%')
          .order('created_at', { ascending: false })
          .limit(50);

        if (error) throw error;
        setUploadHistory(data || []);
      } else {
        const { data, error } = await supabase
          .from('supplier_invoice_uploads')
          .select('id, participant_id, participant_name, ean_code, file_name, upload_date, created_at')
          .eq('participant_id', participant.id)
          .like('file_name', 'CONTRAT_%')
          .order('created_at', { ascending: false })
          .limit(50);

        if (error) throw error;
        setUploadHistory(data || []);
      }
    } catch (error) {
      console.error('Error loading upload history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const toggleParticipantSelection = (id: string) => {
    setSelectedUploadParticipants(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error(t('memberSidebar.supplier.pdfOnly'));
      return;
    }

    const targets = participant?.groupe && groupParticipants.length > 1
      ? groupParticipants.filter(p => selectedUploadParticipants.includes(p.id))
      : [{ id: participant.id, name: participant.name, email: participant.email || user.email, ean_code: participant.ean_code }];

    if (targets.length === 0) {
      toast.error(t('memberUi.selectOneParticipant'));
      return;
    }

    setUploadingFile(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64Content = (reader.result as string).split(',')[1];
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
          const uploadDateStr = format(new Date(), 'yyyy-MM-dd');
          const { data: sessionData } = await supabase.auth.getSession();
          const accessToken = sessionData.session?.access_token;

          for (const target of targets) {
            const cleanName = target.name.replace(/[^a-zA-Z0-9]/g, '_');
            const fileName = `${cleanName}_${uploadDateStr}.pdf`;

            const response = await fetch(`${supabaseUrl}/functions/v1/upload-to-drive`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
              },
              body: JSON.stringify({
                fileName,
                fileContent: base64Content,
                participantName: target.name,
                participantId: target.id,
                participantEmail: target.email || participant.email || user.email,
                uploadedByParticipantId: participant.id,
                eanCode: target.ean_code,
                uploadDate: uploadDateStr
              })
            });

            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.error || 'Upload failed');
            }
          }

          toast.success(targets.length > 1
            ? `Facture envoyée pour ${targets.length} participants`
            : t('memberSidebar.supplier.uploadSuccess')
          );
          await loadUploadHistory();
        } catch (error: any) {
          console.error('Error uploading file:', error);
          toast.error(t('memberSidebar.supplier.uploadError') + ': ' + error.message);
        } finally {
          setUploadingFile(false);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      };
      reader.readAsDataURL(file);
    } catch (error: any) {
      console.error('Error uploading file:', error);
      toast.error(t('memberSidebar.supplier.uploadError') + ': ' + error.message);
      setUploadingFile(false);
    }
  };

  const toggleContractParticipant = (id: string) => {
    setContractSelectedParticipants(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const handleContractUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error(t('memberUi.selectPdf'));
      return;
    }

    const targets = participant?.groupe && groupParticipants.length > 1
      ? groupParticipants
      : [{ id: participant.id, name: participant.name, email: participant.email || user.email, ean_code: participant.ean_code }];

    setUploadingContract(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64Content = (reader.result as string).split(',')[1];
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
          const uploadDateStr = format(new Date(), 'yyyy-MM-dd');
          const { data: sessionData } = await supabase.auth.getSession();
          const accessToken = sessionData.session?.access_token;

          for (const target of targets) {
            const cleanName = target.name.replace(/[^a-zA-Z0-9]/g, '_');
            const fileName = `CONTRAT_${cleanName}_${uploadDateStr}.pdf`;

            const response = await fetch(`${supabaseUrl}/functions/v1/upload-to-drive`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
              },
              body: JSON.stringify({
                fileName,
                fileContent: base64Content,
                participantName: target.name,
                participantId: target.id,
                participantEmail: target.email || participant.email || user.email,
                uploadedByParticipantId: participant.id,
                eanCode: target.ean_code,
                uploadDate: uploadDateStr,
                documentType: 'contract'
              })
            });

            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.error || 'Upload failed');
            }
          }

          toast.success(
            targets.length > 1
              ? t('memberUi.contractSentMany', { count: targets.length })
              : t('memberUi.contractSent')
          );
          setShowContractUpload(false);
          setContractSelectedParticipants([]);
          await loadUploadHistory();
        } catch (error: any) {
          console.error('Error uploading contract:', error);
          toast.error(t('memberUi.sendError', { message: error.message }));
        } finally {
          setUploadingContract(false);
          if (contractFileInputRef.current) {
            contractFileInputRef.current.value = '';
          }
        }
      };
      reader.readAsDataURL(file);
    } catch (error: any) {
      console.error('Error uploading contract:', error);
      toast.error(t('memberUi.sendError', { message: error.message }));
      setUploadingContract(false);
    }
  };

  const handleSendContact = async () => {
    if (!contactForm.message.trim()) {
      toast.error(t('memberSidebar.contact.enterMessage'));
      return;
    }

    setSendingContact(true);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/send-contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          email: participant.email || user.email,
          subject: contactForm.type === 'bug' ? 'Bug Report' :
                   contactForm.type === 'suggestion' ? 'Suggestion' : 'Support Request',
          message: `[${contactForm.type.toUpperCase()}] De: ${participant.name}\n\n${contactForm.message}`,
          type: contactForm.type
        })
      });

      if (!response.ok) throw new Error('Failed to send message');

      toast.success(t('memberSidebar.contact.success'));
      setContactForm({ type: 'support', message: '' });
      setActiveSection(null);
    } catch (error) {
      console.error('Error sending contact:', error);
      toast.error(t('memberSidebar.contact.error'));
    } finally {
      setSendingContact(false);
    }
  };

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('i18nextLng', lang);
    toast.success(t('memberSidebar.language.languageChanged'));
  };

  const formatMonthName = (monthNumber: number, year: number) => {
    const date = new Date(year, monthNumber - 1, 1);
    return format(date, 'MMMM yyyy', { locale: getDateLocale() });
  };

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'profile':
        return (
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#00516F]">{t('memberSidebar.profile.title')}</h3>
              <button onClick={() => setActiveSection(null)} className="text-[#8A9AA5] hover:text-[#51626E]">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              {participant.groupe && groupEans.length > 1 ? (
                <div className="p-3 bg-[#F7FAFB] rounded-lg">
                  <div className="flex items-center space-x-3 mb-2">
                    <Hash className="w-5 h-5 text-[#00516F]" />
                    <p className="text-xs text-[#51626E]">{t('memberSidebar.profile.groupEans')}</p>
                  </div>
                  <div className="relative">
                    <button
                      onClick={() => setShowEanDropdown(!showEanDropdown)}
                      className="w-full flex items-center justify-between px-3 py-2 bg-white border border-[#C9DFE5] rounded-lg text-sm hover:bg-[#F7FAFB] transition-colors"
                    >
                      <span className="text-[#0C1C2C]">{groupEans.length} EAN{groupEans.length > 1 ? 's' : ''}</span>
                      <ChevronDown className={`w-4 h-4 text-[#8A9AA5] transition-transform ${showEanDropdown ? 'rotate-180' : ''}`} />
                    </button>
                    {showEanDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-[#C9DFE5] rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {groupEans.map((item, index) => (
                          <div
                            key={index}
                            className="px-3 py-2 hover:bg-[#F7FAFB] border-b border-[#E3ECEF] last:border-b-0"
                          >
                            <p className="text-xs text-[#51626E] truncate">{item.name}</p>
                            <p className="text-sm font-mono text-[#0C1C2C]">{item.ean}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-start space-x-3 p-3 bg-[#F7FAFB] rounded-lg">
                  <Hash className="w-5 h-5 text-[#00516F] mt-0.5" />
                  <div>
                    <p className="text-xs text-[#51626E]">{t('memberSidebar.profile.eanCode')}</p>
                    <p className="text-sm font-mono text-[#0C1C2C]">{participant.ean_code || '-'}</p>
                  </div>
                </div>
              )}

              <div className="flex items-start space-x-3 p-3 bg-[#F7FAFB] rounded-lg">
                <Calendar className="w-5 h-5 text-[#00516F] mt-0.5" />
                <div>
                  <p className="text-xs text-[#51626E]">{t('memberSidebar.profile.entryDate')}</p>
                  <p className="text-sm text-[#0C1C2C]">
                    {participant.entry_date
                      ? format(parseISO(participant.entry_date), 'dd MMMM yyyy', { locale: getDateLocale() })
                      : '-'}
                  </p>
                </div>
              </div>

              {(() => {
                const isMixedGroup = groupPriceInfo
                  ? (groupPriceInfo.hasConsumers && groupPriceInfo.hasProducers) || (groupPriceInfo.hasConsumers && groupPriceInfo.hasProsumers) || (groupPriceInfo.hasProducers && groupPriceInfo.hasProsumers)
                  : false;

                const showBuySell = isMixedGroup && groupPriceInfo?.prosumerBuyPrice != null && groupPriceInfo?.prosumerSellPrice != null;

                if (showBuySell) {
                  return (
                    <>
                      <div className="flex items-start space-x-3 p-3 bg-[#F7FAFB] rounded-lg">
                        <Euro className="w-5 h-5 text-[#00516F] mt-0.5" />
                        <div>
                          <p className="text-xs text-[#51626E]">{t('memberSidebar.profile.prosumerBuyPrice')}</p>
                          <p className="text-sm text-[#0C1C2C]">
                            {groupPriceInfo!.prosumerBuyPrice ? `${groupPriceInfo!.prosumerBuyPrice} EUR/MWh` : '-'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3 p-3 bg-[#F7FAFB] rounded-lg">
                        <Euro className="w-5 h-5 text-[#00516F] mt-0.5" />
                        <div>
                          <p className="text-xs text-[#51626E]">{t('memberSidebar.profile.prosumerSellPrice')}</p>
                          <p className="text-sm text-[#0C1C2C]">
                            {groupPriceInfo!.prosumerSellPrice ? `${groupPriceInfo!.prosumerSellPrice} EUR/MWh` : '-'}
                          </p>
                        </div>
                      </div>
                    </>
                  );
                }

                const priceValue = groupPriceInfo?.sharedEnergyPrice ?? participant.shared_energy_price ?? null;
                return (
                  <div className="flex items-start space-x-3 p-3 bg-[#F7FAFB] rounded-lg">
                    <Euro className="w-5 h-5 text-[#00516F] mt-0.5" />
                    <div>
                      <p className="text-xs text-[#51626E]">{t('memberSidebar.profile.sharedPrice')}</p>
                      <p className="text-sm text-[#0C1C2C]">
                        {priceValue ? `${priceValue} EUR/MWh` : '-'}
                      </p>
                    </div>
                  </div>
                );
              })()}

              <div className="flex items-start space-x-3 p-3 bg-[#F7FAFB] rounded-lg">
                <Mail className="w-5 h-5 text-[#00516F] mt-0.5" />
                <div>
                  <p className="text-xs text-[#51626E]">{t('memberSidebar.profile.email')}</p>
                  <p className="text-sm text-[#0C1C2C]">{participant.email || user.email || '-'}</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'invoices': {
        const hasMultipleCompanies = groupCompanies.length > 1;
        const isProducer = participant?.type === 'producer';
        const availableMonthKeys = new Set(availableMonths.map(m => `${m.year}-${String(m.monthNumber).padStart(2, '0')}`));

        const years = Array.from(new Set(availableMonths.map(m => m.year))).sort((a, b) => b - a);

        const toggleYear = (year: number) => {
          setExpandedYears(prev => {
            const next = new Set(prev);
            if (next.has(year)) next.delete(year);
            else next.add(year);
            return next;
          });
        };

        const toggleQuarter = (key: string) => {
          setExpandedQuarters(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
          });
        };

        const renderMonthButtons = (monthsForYear: typeof availableMonths, company?: GroupCompany, companyIdx?: number) =>
          monthsForYear.map(monthData => {
            const monthKey = `${monthData.year}-${String(monthData.monthNumber).padStart(2, '0')}`;
            const btnKey = companyIdx !== undefined ? `${companyIdx}-${monthKey}` : monthKey;
            return (
              <button
                key={btnKey}
                onClick={() => handleGenerateInvoice(monthKey, company)}
                disabled={generatingInvoice === btnKey}
                className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-[#F7FAFB] transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <FileCheck className="w-4 h-4 text-[#00516F] flex-shrink-0" />
                  <span className="text-sm text-[#0C1C2C] capitalize">
                    {formatMonthName(monthData.monthNumber, monthData.year)}
                  </span>
                </div>
                {generatingInvoice === btnKey ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-[#00516F]"></div>
                ) : (
                  <Download className="w-4 h-4 text-[#8A9AA5]" />
                )}
              </button>
            );
          });

        const renderQuarterButtons = (year: number, company?: GroupCompany, companyIdx?: number) => {
          return [1, 2, 3, 4].map(quarter => {
            const quarterMonths = getQuarterMonths(year, quarter);
            const quarterStart = quarterMonths[0].monthKey;
            const quarterEnd = quarterMonths[2].monthKey;
            const complete = isQuarterComplete(year, quarter, availableMonthKeys);
            const hasAnyData = quarterMonths.some(m => availableMonthKeys.has(m.monthKey));
            if (!hasAnyData) return null;

            const quarterKey = companyIdx !== undefined ? `${companyIdx}-${year}-Q${quarter}` : `${year}-Q${quarter}`;
            const isExpanded = expandedQuarters.has(quarterKey);

            return (
              <div key={quarterKey} className="border border-[#C9DFE5] rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleQuarter(quarterKey)}
                  className="w-full flex items-center justify-between px-3 py-2.5 bg-[#F7FAFB] hover:bg-[#EEF5F7] transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-[#00516F]" />
                    <span className="text-sm font-semibold text-[#0C1C2C]">{QUARTER_LABELS[quarter - 1]} {year}</span>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-[#8A9AA5] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </button>

                {isExpanded && (
                  <div className="divide-y divide-[#E3ECEF]">
                    {complete ? (
                      <button
                        onClick={() => handleGenerateQuarterInvoice(quarterStart, quarterEnd, company)}
                        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-[#F7FAFB] transition-colors"
                      >
                        <div className="flex items-center space-x-2">
                          <FileCheck className="w-4 h-4 text-[#00516F] flex-shrink-0" />
                          <span className="text-sm text-[#0C1C2C]">{t('memberUi.quarterInvoice')}</span>
                        </div>
                        <Download className="w-4 h-4 text-[#8A9AA5]" />
                      </button>
                    ) : (
                      <div className="divide-y divide-[#E3ECEF]">
                        <button
                          onClick={() => handleGenerateQuarterInvoice(quarterStart, quarterEnd, company)}
                          className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-[#F7FAFB] transition-colors"
                        >
                          <div className="flex items-center space-x-2">
                            <FileCheck className="w-4 h-4 text-[#F59B42] flex-shrink-0" />
                            <span className="text-sm text-[#0C1C2C]">{t('memberUi.quarterInvoice')}</span>
                          </div>
                          <Download className="w-4 h-4 text-[#8A9AA5]" />
                        </button>
                        <div className="px-3 py-2 flex items-center space-x-2 bg-[#FEF4EA]">
                          <AlertTriangle className="w-4 h-4 text-[#F59B42] flex-shrink-0" />
                          <span className="text-xs text-[#B35F0F]">{t('memberUi.quarterIncomplete')}</span>
                        </div>
                      </div>
                    )}

                  </div>
                )}
              </div>
            );
          });
        };

        return (
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#00516F]">{t('memberSidebar.invoices.title')}</h3>
              <button onClick={() => setActiveSection(null)} className="text-[#8A9AA5] hover:text-[#51626E]">
                <X className="w-5 h-5" />
              </button>
            </div>

            {loadingMonths ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#00516F]"></div>
              </div>
            ) : availableMonths.length === 0 ? (
              <div className="text-center py-8 text-[#51626E]">
                <FileText className="w-8 h-8 mx-auto mb-2 text-[#C9DFE5]" />
                <p className="text-sm">{t('memberSidebar.invoices.noInvoices')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {years.map(year => {
                  const monthsForYear = availableMonths.filter(m => m.year === year);
                  const isExpanded = expandedYears.has(year);
                  return (
                    <div key={year} className="border border-[#C9DFE5] rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleYear(year)}
                        className="w-full flex items-center justify-between px-3 py-2.5 bg-[#F7FAFB] hover:bg-[#EEF5F7] transition-colors"
                      >
                        <div className="flex items-center space-x-2">
                          <Calendar className="w-4 h-4 text-[#00516F]" />
                          <span className="text-sm font-semibold text-[#0C1C2C]">{year}</span>
                          <span className="text-xs text-[#51626E]">{t('memberUi.months', { count: monthsForYear.length })}</span>
                        </div>
                        <ChevronDown className={`w-4 h-4 text-[#8A9AA5] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </button>

                      {isExpanded && (
                        <div className="divide-y divide-[#E3ECEF]">
                          {hasMultipleCompanies ? (
                            <div className="p-2 space-y-2">
                              {groupCompanies.map((company, companyIdx) => (
                                <div key={companyIdx} className="border border-[#C9DFE5] rounded-lg overflow-hidden">
                                  <div className="bg-[#F7FAFB] px-3 py-1.5 border-b border-[#E3ECEF]">
                                    <p className="text-xs font-semibold text-[#0C1C2C] truncate">
                                      {company.company_name || (company.company_number ? `N° ${company.company_number}` : t('memberUi.noCompanyNumber'))}
                                    </p>
                                    {company.company_number && company.company_name && (
                                      <p className="text-xs text-[#8A9AA5] font-mono">{company.company_number}</p>
                                    )}
                                  </div>
                                  {isProducer ? (
                                    <div className="p-2 space-y-2">
                                      {renderQuarterButtons(year, company, companyIdx)}
                                    </div>
                                  ) : (
                                    <div className="divide-y divide-[#E3ECEF]">
                                      {renderMonthButtons(monthsForYear, company, companyIdx)}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : isProducer ? (
                            <div className="p-2 space-y-2">
                              {renderQuarterButtons(year)}
                            </div>
                          ) : (
                            renderMonthButtons(monthsForYear)
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      }

      case 'contact':
        return (
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#00516F]">{t('memberSidebar.contact.title')}</h3>
              <button onClick={() => setActiveSection(null)} className="text-[#8A9AA5] hover:text-[#51626E]">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div className="flex space-x-2">
                {[
                  { type: 'support', icon: MessageSquare, label: t('memberSidebar.contact.support') },
                  { type: 'bug', icon: Bug, label: t('memberSidebar.contact.bug') },
                  { type: 'suggestion', icon: Lightbulb, label: t('memberSidebar.contact.suggestion') }
                ].map((item) => (
                  <button
                    key={item.type}
                    onClick={() => setContactForm(prev => ({ ...prev, type: item.type as any }))}
                    className={`flex-1 flex flex-col items-center p-2 rounded-lg transition-colors ${
                      contactForm.type === item.type
                        ? 'bg-[#EEF5F7] border border-[#00516F]'
                        : 'bg-[#F7FAFB] border border-transparent hover:bg-[#EEF5F7]'
                    }`}
                  >
                    <item.icon className={`w-5 h-5 mb-1 ${
                      contactForm.type === item.type ? 'text-[#00516F]' : 'text-[#8A9AA5]'
                    }`} />
                    <span className="text-xs">{item.label}</span>
                  </button>
                ))}
              </div>

              <textarea
                value={contactForm.message}
                onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                placeholder={
                  contactForm.type === 'bug'
                    ? t('memberSidebar.contact.bugPlaceholder')
                    : contactForm.type === 'suggestion'
                    ? t('memberSidebar.contact.suggestionPlaceholder')
                    : t('memberSidebar.contact.supportPlaceholder')
                }
                className="w-full h-24 px-3 py-2 text-sm border border-[#C9DFE5] rounded-lg focus:ring-2 focus:ring-[#00516F]/30 focus:border-transparent resize-none"
              />

              <button
                onClick={handleSendContact}
                disabled={sendingContact || !contactForm.message.trim()}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-[#FCAE0A] text-[#0C1C2C] font-semibold rounded-[10px] hover:bg-[#F0A200] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {sendingContact ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>{t('memberSidebar.contact.send')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        );

      case 'settings':
        return (
          <div className="p-4 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-[#00516F]">{t('memberUi.settingsTitle')}</h3>
              <button onClick={() => setActiveSection(null)} className="text-[#8A9AA5] hover:text-[#51626E]">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div>
              <p className="text-xs font-medium text-[#51626E] uppercase tracking-wider mb-2">{t('memberSidebar.language.title')}</p>
              <div className="space-y-1.5">
                {[
                  { code: 'fr', name: t('memberSidebar.language.french'), flag: '🇫🇷' },
                  { code: 'nl', name: t('memberSidebar.language.dutch'), flag: '🇳🇱' },
                  { code: 'en', name: t('memberSidebar.language.english'), flag: '🇬🇧' }
                ].map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`w-full flex items-center justify-between p-2.5 rounded-lg transition-colors ${
                      i18n.language === lang.code
                        ? 'bg-[#EEF5F7] border border-[#00516F]'
                        : 'bg-[#F7FAFB] border border-transparent hover:bg-[#EEF5F7]'
                    }`}
                  >
                    <div className="flex items-center space-x-2.5">
                      <span className="text-lg">{lang.flag}</span>
                      <span className="text-sm text-[#0C1C2C]">{lang.name}</span>
                    </div>
                    {i18n.language === lang.code && (
                      <Check className="w-4 h-4 text-[#00516F]" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="border-t border-[#E3ECEF]" />

            <div>
              <p className="text-xs text-[#51626E] mb-3 flex items-start space-x-1.5">
                <BarChart2 className="w-3.5 h-3.5 text-[#00516F] flex-shrink-0 mt-0.5" />
                <span>{t('memberSidebar.settings.contractUploadDescription')}</span>
              </p>

              <input
                ref={contractFileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleContractUpload}
                className="hidden"
              />

              <button
                onClick={() => {
                  if (!participant?.groupe) {
                    setContractSelectedParticipants([participant.id]);
                  }
                  contractFileInputRef.current?.click();
                }}
                disabled={uploadingContract}
                className="w-full flex items-center justify-center space-x-2 px-3 py-2.5 bg-white border border-[#C9DFE5] rounded-[10px] text-[#0C1C2C] hover:border-[#00516F] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {uploadingContract ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-[#00516F]"></div>
                ) : (
                  <>
                    <FileSignature className="w-4 h-4 text-[#00516F] flex-shrink-0" />
                    <span className="text-sm font-medium">{t('memberSidebar.settings.uploadContract')}</span>
                  </>
                )}
              </button>
            </div>

            <div>
              <p className="text-xs font-medium text-[#51626E] uppercase tracking-wider mb-2">{t('memberSidebar.settings.uploadHistory')}</p>
              {loadingHistory ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-[#00516F]"></div>
                </div>
              ) : uploadHistory.length === 0 ? (
                <div className="text-center py-4">
                  <Clock className="w-6 h-6 mx-auto mb-1.5 text-[#C9DFE5]" />
                  <p className="text-xs text-[#51626E]">{t('memberSidebar.settings.noUploads')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {uploadHistory.map(record => (
                    <div key={record.id} className="p-2.5 bg-[#F7FAFB] rounded-lg border border-[#E3ECEF]">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs font-medium text-[#0C1C2C] truncate">{record.participant_name}</p>
                        <p className="text-xs text-[#51626E] flex-shrink-0">
                          {format(parseISO(record.created_at), 'dd/MM/yyyy', { locale: getDateLocale() })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <div className="fixed left-0 top-1/2 -translate-y-1/2 z-40 flex">
        <div className="bg-white shadow-lg rounded-r-2xl border border-l-0 border-[#C9DFE5] overflow-hidden">
          <div className="flex flex-col py-2">
            <button
              data-tutorial="tutorial-sidebar-profile"
              onClick={() => setActiveSection(activeSection === 'profile' ? null : 'profile')}
              className={`flex items-center justify-center w-14 h-14 transition-colors ${
                activeSection === 'profile' ? 'bg-[#EEF5F7] text-[#00516F]' : 'text-[#00516F] hover:bg-[#F2F2F3]'
              }`}
              title={t('memberUi.myProfile')}
            >
              <User className="w-6 h-6" />
            </button>

            <button
              data-tutorial="tutorial-sidebar-invoices"
              onClick={() => setActiveSection(activeSection === 'invoices' ? null : 'invoices')}
              className={`flex items-center justify-center w-14 h-14 transition-colors ${
                activeSection === 'invoices' ? 'bg-[#EEF5F7] text-[#00516F]' : 'text-[#00516F] hover:bg-[#F2F2F3]'
              }`}
              title={t('memberUi.myInvoices')}
            >
              <FileText className="w-6 h-6" />
            </button>

            <button
              data-tutorial="tutorial-sidebar-contact"
              onClick={() => setActiveSection(activeSection === 'contact' ? null : 'contact')}
              className={`flex items-center justify-center w-14 h-14 transition-colors ${
                activeSection === 'contact' ? 'bg-[#EEF5F7] text-[#00516F]' : 'text-[#00516F] hover:bg-[#F2F2F3]'
              }`}
              title={t('memberUi.contact')}
            >
              <MessageSquare className="w-6 h-6" />
            </button>

            <button
              data-tutorial="tutorial-sidebar-settings"
              onClick={() => setActiveSection(activeSection === 'settings' ? null : 'settings')}
              className={`flex items-center justify-center w-14 h-14 transition-colors ${
                activeSection === 'settings' ? 'bg-[#EEF5F7] text-[#00516F]' : 'text-[#00516F] hover:bg-[#F2F2F3]'
              }`}
              title={t('memberUi.settingsTitle')}
            >
              <Settings className="w-6 h-6" />
            </button>
          </div>
        </div>

        {activeSection && (
          <div className="bg-white shadow-lg rounded-r-2xl border border-l-0 border-[#C9DFE5] w-[36rem] max-h-[80vh] overflow-y-auto">
            {renderSectionContent()}
          </div>
        )}
      </div>

      {showInvoicePreview && selectedInvoicePeriod && (
        <InvoiceTemplate
          isOpen={showInvoicePreview}
          participant={selectedInvoiceCompany
            ? (selectedInvoiceCompany.participants[0] as any)
            : participant}
          selectedPeriod={selectedInvoicePeriod}
          isAdminOnly={false}
          companyNumberFilter={selectedInvoiceCompany ? selectedInvoiceCompany.company_number : undefined}
          issueDate={selectedInvoiceIssueDate}
          onClose={() => {
            setShowInvoicePreview(false);
            setSelectedInvoiceMonth(null);
            setSelectedInvoiceCompany(null);
            setSelectedInvoicePeriod(null);
            setSelectedInvoiceIssueDate(null);
          }}
        />
      )}
    </>
  );
}

export default MemberSidebar;
